import { useMemo } from 'react';

import { Template } from '@pdfme/common';

import { bindActionCreators, useDispatch, useSelector } from 'third-party';

import { settingEndpoint, settingSingleEndpoint } from 'constants/endpoints';

import { appSelectors } from 'selectors';

import { httpMethod, simplifyBuilder } from 'utils/sra';

import { APISetting, APISettingName } from 'types/api';

export const EMPTY_EMAIL_MESSAGE: any = {
  root: {
    type: 'EmailLayout',
    data: {
      backdropColor: '#F5F5F5',
      canvasColor: '#FFFFFF',
      textColor: '#262626',
      fontFamily: 'MODERN_SANS',
      childrenIds: [],
    },
  },
};

export type GeneralSetting = {
  name: APISettingName;
  value: string;
};

export type SettingsState = {
  emailTemplate: any;
  checkTemplate: Template | null;
  selectedTemplate: APISettingName;
  selectedBlockId: string | null;
  selectedSidebarTab: 'block-configuration' | 'styles';
  selectedMainTab: 'editor' | 'preview';
  selectedScreenSize: 'desktop' | 'mobile';
  loadCheckTemplatePending: boolean;
  saveCheckTemplatePending: boolean;
  loadEmailTemplatePending: boolean;
  loadSettingsPending: boolean;
  saveSettingPending: boolean;
  generalSettings: GeneralSetting[];
  isEmailTemplateReady: boolean;
};

export const settingsState: SettingsState = {
  emailTemplate: EMPTY_EMAIL_MESSAGE,
  checkTemplate: null,
  selectedTemplate: '' as APISettingName,
  selectedBlockId: null,
  selectedSidebarTab: 'styles',
  selectedMainTab: 'editor',
  selectedScreenSize: 'desktop',
  loadCheckTemplatePending: false,
  saveCheckTemplatePending: false,
  loadEmailTemplatePending: false,
  loadSettingsPending: false,
  saveSettingPending: false,
  isEmailTemplateReady: false,
  generalSettings: [
    {
      name: APISettingName.ECheckPrintLimit,
      value: '1',
    },
  ],
};

const builder = simplifyBuilder(settingsState, {});

export const setEmailTemplate = builder.createReduxAction((template: any) => ({
  name: 'setEmailTemplate',
  updater: state => ({
    emailTemplate: { ...state.emailTemplate, ...template },
  }),
}));

export const resetEmailTemplate = builder.createReduxAction(
  (template: any = EMPTY_EMAIL_MESSAGE) => ({
    name: 'resetEmailTemplate',
    updater: () => ({
      emailTemplate: template,
      selectedSidebarTab: 'styles',
      selectedBlockId: null,
    }),
  }),
);

export const setSelectedTemplateBlockId = builder.createReduxAction(
  (selectedBlockId: string | null) => ({
    name: 'setSelectedTemplateBlockId',
    updater: () => ({
      selectedBlockId,
      selectedSidebarTab:
        selectedBlockId === null ? 'styles' : 'block-configuration',
    }),
  }),
);

export const setSelectedSidebarTab = builder.createReduxAction(
  (selectedSidebarTab: 'block-configuration' | 'styles') => ({
    name: 'setSelectedSidebarTab',
    updater: () => ({
      selectedSidebarTab: selectedSidebarTab,
    }),
  }),
);

export const setSelectedScreenSize = builder.createReduxAction(
  (selectedScreenSize: 'desktop' | 'mobile') => ({
    name: 'setSelectedScreenSize',
    updater: () => ({
      selectedScreenSize: selectedScreenSize,
    }),
  }),
);

export const setSelectedMainTab = builder.createReduxAction(
  (selectedMainTab: 'editor' | 'preview') => ({
    name: 'setSelectedMainTab',
    updater: () => ({
      selectedMainTab: selectedMainTab,
    }),
  }),
);

const loadSetting = builder.createServerAction(
  (tenantId: string, name: APISettingName) => ({
    name: 'loadSetting',
    url: settingSingleEndpoint(tenantId, name),
    method: httpMethod.get,
    onSuccess: (state: SettingsState, payload: APISetting) => ({
      generalSettings: state.generalSettings.map(setting =>
        setting.name === payload.name ? payload : setting,
      ),
    }),
  }),
);

const loadSettings = builder.createServerAction((tenantId: string) => ({
  name: 'loadSettings',
  url: settingEndpoint(tenantId),
  method: httpMethod.get,
  onSuccess: (state: SettingsState, payload: APISetting[]) => ({
    generalSettings: payload.map(setting => ({
      name: setting.name,
      value: setting.value,
    })),
  }),
}));

const loadEmailTemplate = builder.createServerAction(
  (tenantId: string, name: APISettingName) => ({
    name: 'loadEmailTemplate',
    url: settingSingleEndpoint(tenantId, name),
    method: httpMethod.get,
    onSuccess: (state: SettingsState, payload: APISetting) => {
      const res = JSON.parse(payload.value)?.json ?? null;
      return {
        emailTemplate: res ?? EMPTY_EMAIL_MESSAGE,
        isEmailTemplateReady: true,
      };
    },
  }),
);

const clearEmailTemplate = builder.createReduxAction(() => ({
  name: 'clearEmailTemplate',
  updater: () => ({
    emailTemplate: EMPTY_EMAIL_MESSAGE,
    isEmailTemplateReady: false,
  }),
}));

export const setSelectedTemplate = builder.createReduxAction(
  (templateName: APISettingName) => ({
    name: 'setSelectedTemplate',
    updater: () => ({
      selectedTemplate: templateName,
    }),
  }),
);

const saveSetting = builder.createServerAction(
  (tenantId: string, setting: APISetting) => ({
    name: 'saveSetting',
    url: settingEndpoint(tenantId),
    body: setting,
    method: httpMethod.post,
    onSuccess: (state: SettingsState, payload: APISetting) => ({
      generalSettings: state.generalSettings.map(it =>
        it.name == payload.name ? payload : it,
      ),
    }),
  }),
);

export const useSettings = () => {
  const dispatch = useDispatch();
  const tenantId = useSelector(appSelectors.tenantId);

  return useMemo(
    () =>
      bindActionCreators(
        {
          loadSetting: loadSetting.bind(null, tenantId),
          loadSettings: loadSettings.bind(null, tenantId),
          saveSetting: saveSetting.bind(null, tenantId),
          loadTenantEmailTemplate: loadEmailTemplate,
          loadEmailTemplate: loadEmailTemplate.bind(null, tenantId),
          setSelectedTemplate,
          setEmailTemplate,
          setSelectedTemplateBlockId,
          setSelectedSidebarTab,
          setSelectedMainTab,
          setSelectedScreenSize,
          resetEmailTemplate,
          clearEmailTemplate,
        },
        dispatch,
      ),
    [dispatch, tenantId],
  );
};

export const settingsReducer = builder.getReducers();
